import { commandCenterPath } from '../commandCenterRoutes'
// ============================================================
// V2 Operations — internal mini-portal registry.
//
// PHASE_2F: every Operations card opens an INTERNAL Square Code V2 mini portal
// at /square/command-center/operations/<id>. Each portal is a workable
// area (work queue, primary actions, working log, receipts, blockers, next
// actions) rendered in the same V2 command-deck flow — never a decorative
// landing page, never a route to another domain.
//
// Honesty rules for this file (do not weaken):
//   - status is the TRUTH about the module, from this vocabulary only:
//       OPEN                — fully reachable + usable in V2 today
//       WORKSPACE_READY     — a working V2 surface exists (local preview) to open
//       ROUTE_READY         — an existing production surface backs this; portal
//                             links to it honestly (secondary, labelled)
//       PLANNED             — not built yet; portal is an honest planning shell
//       SETUP_REQUIRED      — needs a provider/credential before it can run
//       BACKEND_DEFERRED    — UI/route exists; persistence intentionally deferred
//       INTEGRATION_DEFERRED— depends on an external integration held out of scope
//   - queue items are ONLY ever labelled Template/Example/Suggested — NEVER
//     rendered to look like real saved client/job/project records.
//   - relatedSurface is a SECONDARY, clearly-labelled onward link, never the
//     card's primary target. The card always routes to this internal portal.
// ============================================================

export type OperationsModuleStatus =
  | 'OPEN'
  | 'WORKSPACE_READY'
  | 'ROUTE_READY'
  | 'PLANNED'
  | 'SETUP_REQUIRED'
  | 'BACKEND_DEFERRED'
  | 'INTEGRATION_DEFERRED'

export interface OperationsQueueItem {
  /** Always prefixed with its kind in the UI so it never reads as real data. */
  kind: 'Template' | 'Example' | 'Suggested'
  label: string
  meta?: string
}

export interface OperationsQueue {
  title: string
  /** Honest empty-state copy shown when there are no real records to show. */
  emptyLabel: string
  /** Optional labelled templates/examples — NEVER real saved records. */
  items?: OperationsQueueItem[]
}

export interface OperationsAction {
  label: string
  /**
   * local    — a non-persistent working action (planning/notes); labelled.
   * route    — navigates to a real internal V2 route.
   * deferred — shown disabled with an honest reason (no fake execution).
   */
  kind: 'local' | 'route' | 'deferred'
  to?: string
  note?: string
}

export interface OperationsRelatedSurface {
  label: string
  to: string
  /** True when the surface lives in the legacy /square shell (labelled as such). */
  legacy?: boolean
}

export interface OperationsModule {
  id: string
  label: string
  /** Internal V2 route — always /square/command-center/operations/<id>. */
  path: string
  accent: string
  status: OperationsModuleStatus
  /** "What this portal is for". */
  purpose: string
  queue: OperationsQueue
  primaryActions: OperationsAction[]
  /** Prompt for the local working-log textarea (non-persistent, labelled). */
  notesPrompt: string
  receipts: { title: string; emptyLabel: string }
  blockers: string[]
  nextActions: string[]
  /** Honest one-line backend/persistence status. */
  backendStatus: string
  relatedSurface?: OperationsRelatedSurface
}

const BASE = commandCenterPath('operations')
const p = (id: string) => `${BASE}/${id}`

export const OPERATIONS_MODULES: OperationsModule[] = [
  {
    id: 'customer-discovery',
    label: 'Customer Discovery',
    path: p('customer-discovery'),
    accent: '#2563eb',
    status: 'ROUTE_READY',
    purpose:
      'Capture inbound discovery, draft the first client message, and convert a real submission into a project. The V2 discovery workroom is the working surface — persisted and OLIVIUM-audited; this portal is its Operations home.',
    queue: {
      title: 'Discovery inbox',
      emptyLabel: 'This portal does not render a live discovery queue — open the workroom below for real invites and submissions.',
    },
    primaryActions: [
      { label: 'Open the discovery workroom', kind: 'route', to: commandCenterPath('intake'), note: 'Real invites + submissions, persisted to sq_intake_invites/sq_support_requests.' },
      { label: 'Draft an intake message', kind: 'local', note: 'Opens the working log below — non-persistent draft.' },
      { label: 'Convert to project', kind: 'route', to: commandCenterPath('intake'), note: 'Opens the workroom to convert a real submission into a project — audited, not fabricated.' },
    ],
    notesPrompt: 'Draft the discovery message or capture what a prospect asked for…',
    receipts: { title: 'Receipts & links', emptyLabel: 'Discovery + conversion receipts are written to OLIVIUM when you send an invite or convert a submission — this portal does not mirror them yet.' },
    blockers: [],
    nextActions: ['Open the discovery workroom to send a private workspace link or convert a submission', 'Draft the first client message in the working log'],
    backendStatus: 'ROUTE_READY — discovery persists to sq_intake_invites/sq_support_requests and converts to a real sq_projects/sq_clients record, all OLIVIUM-audited, via the discovery workroom.',
    relatedSurface: { label: 'V2 discovery workroom', to: commandCenterPath('intake') },
  },
  {
    id: 'client-intake',
    label: 'Client Intake',
    path: p('client-intake'),
    accent: '#0891b2',
    status: 'ROUTE_READY',
    purpose:
      'Run guided client intake and track submitted forms live. A production secure-intake surface already exists; this portal is its Operations home and readiness board.',
    queue: {
      title: 'Submitted intake forms',
      emptyLabel: 'No submitted intake forms yet.',
    },
    // Primary actions stay inside V2. The legacy production surface is a
    // secondary, clearly-labelled fallback in relatedSurface (see below).
    primaryActions: [
      { label: 'Plan the next client', kind: 'local', note: 'Use the working log to plan the next intake — non-persistent.' },
    ],
    notesPrompt: 'Plan the next client intake, required fields, or follow-ups…',
    receipts: { title: 'Intake receipts', emptyLabel: 'Submitted intakes are audited in OLIVIUM — this portal does not mirror them yet.' },
    blockers: ['Deciding (resolve/close) or converting a submission still happens on the production intake surface — this portal shows the queue live but is read-only.'],
    nextActions: ['Open production intake to decide or convert a submission', 'Open production intake to send/collect a new intake'],
    backendStatus: 'ROUTE_READY — this portal reads the live sq_support_requests review queue via the same audited API the production surface uses; deciding/converting stays on the production surface.',
    relatedSurface: { label: 'Client Intake workroom', to: commandCenterPath('operations/client-intake') },
  },
  {
    id: 'post-job',
    label: 'Post Job',
    path: p('post-job'),
    accent: '#7c3aed',
    status: 'ROUTE_READY',
    purpose:
      'Draft a job, check publish readiness, and track approval + receipt status. The build-now execute layer already exists; this portal is its Operations home and drafting area.',
    queue: {
      title: 'Posted jobs',
      emptyLabel: 'No posted jobs shown here yet — the live job list lives in the execute layer.',
    },
    // Primary stays in V2; the legacy execute layer is a labelled fallback below.
    primaryActions: [
      { label: 'Draft a job (local)', kind: 'local', note: 'Draft job scope in the working log — non-persistent until posted through the execute layer.' },
    ],
    notesPrompt: 'Draft the job: title, scope, acceptance criteria, approval gate…',
    receipts: { title: 'Approval & receipts', emptyLabel: 'Approval and receipt records appear in the execute layer once a job is posted.' },
    blockers: ['Publish readiness is checked in the execute layer, not this portal (yet).'],
    nextActions: ['Draft the job scope here, then post it in the execute layer', 'Confirm the approval gate before posting'],
    backendStatus: 'ROUTE_READY — real posting, approvals, and receipts live in the execute layer; this portal drafts and links, it does not post.',
  },
  {
    id: 'workflows',
    label: 'Workflows',
    path: p('workflows'),
    accent: '#6366f1',
    status: 'ROUTE_READY',
    purpose:
      'Review recent workflow runs and sketch new templates. In-house execution is available via the native Square Code workflow runtime (sc_workflows/sc_workflow_executions) — this portal shows real runs live and links to the full surface to trigger new ones. The optional n8n integration remains deferred.',
    queue: {
      title: 'Recent workflow runs',
      emptyLabel: 'No workflow runs yet.',
    },
    // Primary stays in V2; the legacy workflow surface is a labelled fallback below.
    primaryActions: [
      { label: 'Sketch a workflow', kind: 'local', note: 'Outline steps in the working log — non-persistent, does not execute.' },
    ],
    notesPrompt: 'Outline the workflow: trigger → steps → outputs…',
    receipts: { title: 'Run receipts', emptyLabel: 'No run receipts shown here yet — open a run on the Workflows surface for its full step timeline.' },
    blockers: ['Triggering or resuming a workflow run still happens on the Workflows surface — this portal shows recent runs live but does not trigger new ones (yet).'],
    nextActions: ['Sketch the workflow here', 'Open the Workflows surface to trigger or resume a run'],
    backendStatus: 'ROUTE READY · Native Square Code workflow runtime — in-house execution is available: runs execute via the existing in-house executor (sc_workflows/sc_workflow_executions/sc_workflow_step_runs, driven by api/workflows + executeWorkflow) and this portal shows real runs live. Triggering new runs stays on the Workflows surface. Optional n8n integration remains deferred.',
  },
  {
    id: 'project-board',
    label: 'Project Board',
    path: p('project-board'),
    accent: '#ec4899',
    status: 'ROUTE_READY',
    purpose:
      'Review boards, active lanes, and the send-to-job seam. Personal boards with agent managers already exist; this portal is their Operations home and shows the board list live.',
    queue: {
      title: 'Active boards',
      emptyLabel: 'No boards yet — create one on the project-board surface.',
    },
    // Primary stays in V2; the legacy project-board surface is a labelled fallback below.
    primaryActions: [
      { label: 'Plan a board', kind: 'local', note: 'Sketch lanes and cards in the working log — non-persistent.' },
    ],
    notesPrompt: 'Plan the board: lanes, cards, owners, send-to-job candidates…',
    receipts: { title: 'Board activity', emptyLabel: 'Board activity and receipts live in the project-board surface.' },
    blockers: ['Creating, moving, or assigning cards still happens on the project-board surface — this portal shows the board list live but is read-only.'],
    nextActions: ['Open project boards to create or update a real board', 'Plan lanes here before creating the board'],
    backendStatus: 'ROUTE_READY — this portal reads the live board list via the same audited API the project-board surface uses; creating/moving cards stays on that surface.',
    relatedSurface: { label: 'Project portfolio', to: commandCenterPath('projects') },
  },
  {
    id: 'project-setup',
    label: 'Project Setup',
    path: p('project-setup'),
    accent: '#14b8a6',
    status: 'ROUTE_READY',
    purpose:
      'Walk a setup checklist and enter the new-project command flow. The V2 Projects portfolio is the working surface — create persists for real via the Portal Factory; this portal is its Operations home and readiness checklist.',
    queue: {
      title: 'Setup checklist',
      emptyLabel: 'Start a setup — the checklist below is a suggested structure, not saved state.',
      items: [
        { kind: 'Suggested', label: 'Name + slug + client', meta: 'identity' },
        { kind: 'Suggested', label: 'Pick a project kind / preset', meta: 'readiness' },
        { kind: 'Suggested', label: 'Attach operator metadata_json', meta: 'labels' },
      ],
    },
    primaryActions: [
      { label: 'Open the projects command deck', kind: 'route', to: commandCenterPath('projects'), note: 'V2 Projects portfolio — list, + New Project, project command deck (persists via the Portal Factory).' },
      { label: 'Draft project config', kind: 'local', note: 'Draft the project config in the working log — non-persistent.' },
    ],
    notesPrompt: 'Draft the project: name, slug, client, kind, metadata labels…',
    receipts: { title: 'Setup receipts', emptyLabel: 'Project-created receipts are written to OLIVIUM when you create a project in the deck — this portal does not mirror them yet.' },
    blockers: [],
    nextActions: ['Open the projects command deck to start a project', 'Confirm the project kind + preset before creating'],
    backendStatus: 'ROUTE_READY — the V2 Projects deck persists a real project via the Portal Factory (sq_project_portals), receipted and OLIVIUM-audited.',
    relatedSurface: { label: 'V2 Projects command deck', to: commandCenterPath('projects') },
  },
  {
    id: 'onboarding',
    label: 'Onboarding Center',
    path: p('onboarding'),
    accent: '#f59e0b',
    status: 'PLANNED',
    purpose:
      'Run guided onboarding for agents, clients, and team — a checklist-driven setup area. Secure invite creation (client/team access) is real today; broader checklist-driven automation is not built yet.',
    queue: {
      title: 'Onboarding checklist',
      emptyLabel: 'No onboarding runs yet — checklist-driven onboarding automation is planned, not built.',
      items: [
        { kind: 'Template', label: 'Invite → account → workspace access', meta: 'client onboarding' },
        { kind: 'Template', label: 'Role → permissions → first task', meta: 'team onboarding' },
        { kind: 'Template', label: 'Governed-agent enable → autonomy band', meta: 'agent onboarding' },
      ],
    },
    primaryActions: [
      { label: 'Plan an onboarding run', kind: 'local', note: 'Outline the steps in the working log — non-persistent.' },
    ],
    notesPrompt: 'Outline the onboarding run: who, steps, access, first task…',
    receipts: { title: 'Onboarding receipts', emptyLabel: 'Invite receipts are audited in OLIVIUM on the Secure Invites surface — this portal does not mirror them yet.' },
    blockers: ['Checklist-driven onboarding automation (multi-step runs) is not built — this is otherwise an honest planning shell, except client/team secure-invite creation, which is real today (see Secure Invites).'],
    nextActions: ['Draft the onboarding steps here', 'Open Secure Invites to create a real client/team invite'],
    backendStatus: 'PLANNED — secure invite creation is real and audited via sq_membership_invites; broader checklist-driven onboarding automation is not built.',
    relatedSurface: { label: 'Secure Invites', to: '/square/onboarding/secure-invites', legacy: true },
  },
  {
    id: 'job-runs',
    label: 'Job Runs',
    path: p('job-runs'),
    accent: '#6366f1',
    status: 'ROUTE_READY',
    purpose:
      'Review each job\'s live lifecycle state. Jobs execute via the engine-v4 operational runtime (sq_post_jobs + sq_post_job_runs); this portal shows real state, the Post Jobs surface holds the full timeline.',
    queue: {
      title: 'Jobs · live state',
      emptyLabel: 'No jobs yet — post one on the Post Jobs surface.',
    },
    primaryActions: [
      { label: 'Plan a run', kind: 'local', note: 'Note what a run should do in the working log — non-persistent, executes nothing.' },
    ],
    notesPrompt: 'Note the intended run: job, inputs, expected outputs, checks…',
    receipts: { title: 'Logs & receipts', emptyLabel: 'Run logs and receipts are tracked per-job — open a job on the Post Jobs surface for its full timeline.' },
    blockers: ['This portal shows each job\'s live lifecycle state; open the Post Jobs surface for the full run timeline, status events, and receipts.'],
    nextActions: ['Plan a run here', 'Open the Post Jobs surface for a job\'s full timeline and receipts'],
    backendStatus: 'ROUTE_READY — jobs execute via the engine-v4 operational runtime (sq_post_jobs + sq_post_job_runs); this portal shows each job\'s live lifecycle state read via the same audited API the Post Jobs surface uses.',
  },
  {
    // PHASE_2D: real persisted workflow. This card opens the dedicated
    // DailyMeetingsWorkroom (not the generic mini portal) — see the explicit
    // /operations/daily-meetings route. Meetings persist to sq_daily_meetings.
    id: 'daily-meetings',
    label: 'Daily Meetings',
    path: p('daily-meetings'),
    accent: '#8b5cf6',
    status: 'WORKSPACE_READY',
    purpose:
      'Run the daily meeting as a real persisted record — decisions, blockers, action items, readiness changes, and receipts save to sq_daily_meetings (owner-scoped, audited). Cal.com scheduling stays a future integration.',
    queue: {
      title: "Today's meeting",
      emptyLabel: "No past meetings yet — create today's meeting in the workroom to start the record (persisted, never fabricated).",
      items: [
        { kind: 'Suggested', label: 'Decisions', meta: 'what we decided' },
        { kind: 'Suggested', label: 'Blockers', meta: 'what is stuck' },
        { kind: 'Suggested', label: 'Action items', meta: 'who does what next' },
        { kind: 'Suggested', label: 'Readiness changes', meta: 'what moved on the ladder' },
      ],
    },
    primaryActions: [
      { label: 'Open the meetings workroom', kind: 'route', to: p('daily-meetings'), note: 'Create/edit real persisted meetings (sq_daily_meetings), owner-scoped + audited.' },
      { label: 'Schedule via Cal.com', kind: 'deferred', note: 'Cal.com is a future integration only — intentionally not wired.' },
    ],
    notesPrompt: 'Capture: decisions, blockers, action items, readiness changes…',
    receipts: { title: 'Past meeting receipts', emptyLabel: 'Past meetings appear in the workroom, newest first — persisted per record.' },
    blockers: ['Cal.com scheduling is a future integration only — intentionally not wired.'],
    nextActions: ["Open the meetings workroom to create today's meeting", 'Add decisions, blockers, and action items — they persist'],
    backendStatus: 'WORKSPACE_READY — meetings persist to sq_daily_meetings when signed into the live cockpit; every write is audited. Cal.com scheduling is INTEGRATION_DEFERRED (future only).',
  },
  {
    // PHASE_2Q: real command-bar entry point. This card opens the dedicated
    // OperationsAgentCommandCenter (not the generic mini portal) — see the
    // explicit /operations/agent-command route. Dispatch reuses the existing
    // command registry; the approval queue reads sq_agent_action_requests
    // live via the existing Agent Workspace API.
    id: 'agent-command',
    label: 'Agent Command',
    path: p('agent-command'),
    accent: '#22c55e',
    status: 'OPEN',
    purpose:
      'Dispatch a typed command through the canonical command registry and review the live agent-approval queue. Approving or rejecting a request stays on the Agent Workspace surface — a real entry point, not a second dispatcher.',
    queue: {
      title: 'Agent approval queue',
      emptyLabel: 'No agent action requests yet.',
    },
    primaryActions: [
      { label: 'Open the command center workroom', kind: 'route', to: p('agent-command'), note: 'Type a command or review the live approval queue.' },
    ],
    notesPrompt: 'Note the command intent or the approval decision context…',
    receipts: { title: 'Command + approval receipts', emptyLabel: 'Command dispatch and approval decisions are audited in OLIVIUM — this portal reads the live approval queue but not receipts directly.' },
    blockers: ['Approving or rejecting a request still happens on the Agent Workspace surface — this workroom dispatches commands and shows the queue live, but does not decide requests.'],
    nextActions: ['Type a command to dispatch it through the real command registry', 'Open Agent Workspace to approve or reject a pending request'],
    backendStatus: 'OPEN — dispatch runs through the existing /api/commands/dispatch registry (sq_commands); the approval queue reads sq_agent_action_requests live via the existing Agent Workspace API.',
    relatedSurface: { label: 'Agents overview', to: commandCenterPath('agents') },
  },
]

export function getOperationsModule(id: string | undefined): OperationsModule | undefined {
  if (!id) return undefined
  return OPERATIONS_MODULES.find((m) => m.id === id.toLowerCase())
}

export const OPERATIONS_MODULE_IDS = OPERATIONS_MODULES.map((m) => m.id)

/** The canonical Operations home route. */
export const OPERATIONS_HOME_PATH = BASE

/**
 * Operations surface accent — the Operations home title + leading dot color.
 * Lives here (a non-guarded module) so the converted .tsx surfaces pass an
 * accent to CommandCenterShell without a hex literal in the component.
 */
export const OPS_ACCENT = '#2563eb'

/** Agent Command surface accent (matches the agent-command module accent). */
export const AGENT_COMMAND_ACCENT = '#22c55e'

/**
 * Daily Meetings surface accent (matches the daily-meetings module accent).
 * Lives here (a non-guarded module) so the converted DailyMeetingsWorkroom.tsx
 * passes the violet accent to CommandCenterShell + `--sqv-accent` without a hex
 * literal in the component.
 */
export const DAILY_MEETINGS_ACCENT = '#8b5cf6'
